import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { applicationIntakes, kitsConversations } from "@wags/db/schema";
import { getListingsV2ByIds } from "@wags/db/v2";
import {
    type ApplicationForm,
    type ApplicationFormContent,
    type ApplicationFormField,
    type ApplicationIntakeAnswers,
    ApplicationIRSchema,
    applicationFormSchema,
    applicationIntakeFileSchema,
    applicationIntakeTokenSchema,
    submitApplicationIntakeSchema,
} from "@wags/application-intake/application-intake.contract.ts";
import { resolveApplicationFormPath } from "@wags/application-intake/resolve-application-form-path.ts";
import {
    decryptApplicationIntakeAnswers,
    hashApplicationIntakeToken,
} from "@wags/application-intake/application-intake-crypto.ts";
import { latestSupplementalQuestions } from "@wags/application-intake/application-submission-store.ts";
import { eq } from "drizzle-orm";
import { submitApplicationIntakeAnswers } from "./submit.server.ts";
import { readApplicationOwnerPhone } from "./application-phone-sign-in.server.ts";
import { resolveViewerProfileIdentity } from "@/lib/core/auth";
import { getDb } from "@/lib/core/db.server";
import { validateApplicationFieldAnswer } from "./application-answer-validation";
import { isApplicationConditionMet } from "./application-conditions";
import { storeApplicationIntakeFile } from "./application-file-storage.server";
import { applicationUploadRejection } from "./application-upload-rejection.ts";
import {
    APPLICATION_ACCESS_DENIED_MESSAGES,
    APPLICATION_SUBMIT_BLOCKED_MESSAGES,
    resolveApplicationIntakeAccess,
} from "./application-intake-access";

function contentToText(content: ApplicationFormContent | undefined) {
    if (!content) return "";
    if (typeof content === "string") return content;
    return content.segments
        .map((segment) =>
            "text" in segment ? segment.text : `{${segment.id}}`,
        )
        .join("");
}

function applicationAnswerValidationError(
    field: ApplicationFormField,
    value: ApplicationIntakeAnswers[string] | undefined,
    required: boolean,
) {
    try {
        validateApplicationFieldAnswer(field, value, required);
        return null;
    } catch (error) {
        return error instanceof Error
            ? error.message
            : contentToText(field.label) || field.id;
    }
}

function validateRequiredAnswers(
    form: ApplicationForm,
    answers: ApplicationIntakeAnswers,
) {
    const missing: string[] = [];
    for (const section of form.sections) {
        if (!isApplicationConditionMet(section.visibleWhen, answers)) continue;
        const visibleFields = section.fields.filter((field) =>
            isApplicationConditionMet(field.visibleWhen, answers),
        );
        if (!section.repeatable) {
            for (const field of visibleFields) {
                const required =
                    field.required ||
                    (field.requiredWhen !== undefined &&
                        isApplicationConditionMet(field.requiredWhen, answers));
                const message = applicationAnswerValidationError(
                    field,
                    answers[field.id],
                    required,
                );
                if (message) missing.push(message);
            }
            continue;
        }

        const answerCount = Math.max(
            0,
            ...visibleFields.map((field) => {
                const value = answers[field.id];
                return Array.isArray(value) ? value.length : 0;
            }),
        );
        const minimum = section.repeatable.minimum ?? 0;
        if (answerCount < minimum) {
            missing.push(
                `${contentToText(section.title) || section.id} (${minimum} required)`,
            );
        }
        for (let index = 0; index < answerCount; index += 1) {
            for (const field of visibleFields) {
                const required =
                    field.required ||
                    (field.requiredWhen !== undefined &&
                        isApplicationConditionMet(field.requiredWhen, answers));
                const values = answers[field.id];
                const value = Array.isArray(values) ? values[index] : undefined;
                const message = applicationAnswerValidationError(
                    field,
                    value,
                    required,
                );
                if (message) {
                    missing.push(
                        `${message} (${section.repeatable.itemLabel ?? "Item"} ${index + 1})`,
                    );
                }
            }
        }
    }
    if (missing.length > 0) {
        throw new Error(`Please answer: ${missing.join(", ")}`);
    }
}

async function loadIntake(token: string) {
    const db = await getDb();
    const tokenHash = await hashApplicationIntakeToken(token);
    const intake = await db.query.applicationIntakes.findFirst({
        where: eq(applicationIntakes.tokenHash, tokenHash),
    });
    if (!intake) throw new Error("Application form not found.");
    if (intake.expiresAt.getTime() <= Date.now()) {
        throw new Error(APPLICATION_SUBMIT_BLOCKED_MESSAGES.expired);
    }
    const applicationIr = ApplicationIRSchema.parse(intake.applicationIr);
    const resolved = resolveApplicationFormPath(
        applicationIr,
        intake.applicationUrl,
    );
    if (!resolved) throw new Error("This application form is not available.");
    const supplemental = await latestSupplementalQuestions(db, intake.id);
    const form = applicationFormSchema.parse({
        ...resolved.form,
        sections: [...resolved.form.sections, ...supplemental],
    });
    const [listing] = await getListingsV2ByIds(db, [intake.listingId], {
        selection: "dogName",
    });
    return {
        db,
        dogName: listing?.dogNames?.[0] ?? "this dog",
        form,
        intake,
    };
}

async function loadAuthorizedIntake(token: string) {
    const loaded = await loadIntake(token);
    const identity = await resolveViewerProfileIdentity();
    if (!identity) {
        throw new Error(APPLICATION_ACCESS_DENIED_MESSAGES.signed_out);
    }
    const access = resolveApplicationIntakeAccess({
        isAuthenticated: identity.isAuthenticated,
        ownerProfileId: loaded.intake.userProfileId,
        viewerProfileId: identity.appUserId,
    });
    if (access === "signed_out") {
        throw new Error(APPLICATION_ACCESS_DENIED_MESSAGES.signed_out);
    }
    if (access === "wrong_account") {
        throw new Error(APPLICATION_ACCESS_DENIED_MESSAGES.wrong_account);
    }
    return { ...loaded, identity };
}

export const getApplicationIntake = createServerFn({ method: "GET" })
    .inputValidator(applicationIntakeTokenSchema)
    .handler(async ({ data: token }) => {
        const { dogName, form, intake } = await loadIntake(token);
        const identity = await resolveViewerProfileIdentity();
        const access = identity
            ? resolveApplicationIntakeAccess({
                  isAuthenticated: identity.isAuthenticated,
                  ownerProfileId: intake.userProfileId,
                  viewerProfileId: identity.appUserId,
              })
            : "signed_out";
        if (access === "signed_out")
            return {
                access,
                phoneSignIn: (await readApplicationOwnerPhone(intake)) !== null,
            };
        if (access === "wrong_account") return { access };
        return {
            access,
            carriedAnswers:
                intake.status === "draft" && intake.answersEncrypted
                    ? await decryptApplicationIntakeAnswers(
                          intake.answersEncrypted,
                      )
                    : {},
            dogName,
            form,
            status: intake.status,
        };
    });

export const uploadApplicationIntakeFile = createServerFn({ method: "POST" })
    .inputValidator((data: FormData) => {
        if (!(data instanceof FormData)) throw new Error("Expected FormData.");
        const file = data.get("file");
        const fieldId = data.get("fieldId");
        const token = applicationIntakeTokenSchema.parse(data.get("token"));
        if (!(file instanceof File))
            throw new Error("Choose a file to upload.");
        if (typeof fieldId !== "string" || !fieldId) {
            throw new Error("Application field is required.");
        }
        return { fieldId, file, token };
    })
    .handler(async ({ data }) => {
        const { fieldId, file, token } = data;
        const { form, intake } = await loadAuthorizedIntake(token);
        if (intake.status !== "draft") {
            throw new Error(
                APPLICATION_SUBMIT_BLOCKED_MESSAGES.already_submitted,
            );
        }
        const field = form.sections
            .flatMap((section) => section.fields)
            .find((candidate) => candidate.id === fieldId);
        if (!field || field.type !== "file") {
            throw new Error("This application does not accept that file.");
        }
        const rejection = applicationUploadRejection(field, file);
        if (rejection) throw new Error(rejection);
        const safeName = file.name.replaceAll(/[^a-zA-Z0-9._-]/g, "-");
        const hostname = new URL(getRequest().url).hostname;
        const blob = await storeApplicationIntakeFile({
            fieldId: field.id,
            file,
            intakeId: intake.id,
            local:
                hostname === "localhost" ||
                hostname === "127.0.0.1" ||
                hostname === "[::1]",
            safeName,
        });
        return applicationIntakeFileSchema.parse({
            contentType: file.type || "application/octet-stream",
            name: file.name,
            pathname: blob.pathname,
            size: file.size,
        });
    });

export const submitApplicationIntake = createServerFn({ method: "POST" })
    .inputValidator(submitApplicationIntakeSchema)
    .handler(async ({ data }) => {
        const { answers, idempotencyKey, token } = data;
        const { db, dogName, form, intake } = await loadAuthorizedIntake(token);
        if (!intake.kitsConversationId) {
            throw new Error(
                "This application is not connected to a Kits conversation.",
            );
        }
        validateRequiredAnswers(form, answers);
        const confirmation = `Got it! I'm sending your application for ${dogName} now and I'll text you as soon as it's in.`;
        await submitApplicationIntakeAnswers({
            answers,
            applicationIntakeId: intake.id,
            confirmationText: confirmation,
            conversationId: intake.kitsConversationId,
            idempotencyKey,
            listingId: intake.listingId,
            userProfileId: intake.userProfileId,
        });
        const conversation = await db.query.kitsConversations.findFirst({
            where: eq(kitsConversations.id, intake.kitsConversationId),
        });
        if (!conversation) throw new Error("Kits conversation not found.");
        return {
            smsUrl: conversation.providerLineId
                ? `sms:${conversation.providerLineId}`
                : null,
        };
    });

export type { ApplicationFormField };
